import {
	OnboardingTourState,
	OnboardingTourStepStatus,
	TourStepId,
} from "@/pages/Private/redux/onboardingTour/schema";
import { STEP_ORDER } from "@/pages/Private/pages/OnboardingTour/types";

/**
 * The step the sticky ready-to-review reminder should surface (PREC-3719 §9):
 * the first step, in tour order, whose async generation is READY (finished but
 * not yet reviewed). Server-computed `stepStatus` already encodes that — including
 * that a generation which predates the user's tour is the workspace's existing
 * configuration and reports IDLE, never READY.
 *
 * Only steps the tour still walks through qualify — the background generations
 * (scoring, Deep Research, the Knowledge Base) have no step left to review them
 * in, so nothing is surfaced for them.
 */
export const getReadyStep = (state: OnboardingTourState): TourStepId | null => {
	for (const step of STEP_ORDER) {
		if (state.stepStatus?.[step] === OnboardingTourStepStatus.READY) {
			return step;
		}
	}

	return null;
};

/**
 * The background generation whose latest run failed and can be retried from the bar
 * (PREC-4174). Only the Knowledge Base today: it is the one build a user can re-run
 * themselves, and the one that failed silently for five customers in a week.
 */
export const getFailedStep = (state: OnboardingTourState): TourStepId | null =>
	state.stepStatus?.[TourStepId.KNOWLEDGE] === OnboardingTourStepStatus.FAILED
		? TourStepId.KNOWLEDGE
		: null;

/**
 * True while at least one step's generation is still running — the reminder polls
 * the tour state only in that window so it flips to READY on its own.
 */
export const hasPendingGeneration = (state: OnboardingTourState): boolean =>
	Object.values(state.stepStatus ?? {}).some(
		(status) => status === OnboardingTourStepStatus.RUNNING
	);
